import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, BookOpen, Briefcase, FileText, ArrowRight } from "lucide-react";
import api from "../../lib/api";

const STATUS_COLORS = {
  "New Lead": "bg-blue-500/15 text-blue-400",
  Contacted: "bg-yellow-500/15 text-yellow-400",
  Interested: "bg-emerald-500/15 text-emerald-400",
  Counselling: "bg-purple-500/15 text-purple-400",
  "Follow-up": "bg-orange-500/15 text-orange-400",
  Converted: "bg-[#10B981]/15 text-[#10B981]",
  "Not Interested": "bg-red-500/15 text-red-400",
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats) return <p className="text-sm text-[#9CA3AF]">Loading dashboard...</p>;

  const cards = [
    { label: "Total Leads", value: stats.leads_total, icon: Users, to: "/admin/leads", testid: "stat-leads" },
    { label: "New Leads", value: stats.leads_by_status["New Lead"] || 0, icon: Users, to: "/admin/leads", testid: "stat-new-leads" },
    { label: "Courses", value: stats.courses, icon: BookOpen, to: "/admin/courses", testid: "stat-courses" },
    { label: "Applications", value: stats.applications, icon: Briefcase, to: "/admin/applications", testid: "stat-applications" },
    { label: "Blog Posts", value: stats.posts, icon: FileText, to: "/admin/blog", testid: "stat-posts" },
  ];

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-display font-extrabold text-2xl text-[#F9FAFB]">Dashboard</h1>
      <p className="text-sm text-[#9CA3AF] mt-1">Enquiry-to-admission funnel at a glance.</p>

      <div className="mt-7 grid grid-cols-2 lg:grid-cols-5 gap-4">
        {cards.map((c) => (
          <Link
            key={c.label}
            to={c.to}
            data-testid={c.testid}
            className="bg-[#111827] border border-[#1F2937] rounded-xl p-5 hover:border-[#10B981]/50 transition-colors"
          >
            <c.icon size={18} className="text-[#10B981]" />
            <p className="font-mono-sn font-bold text-3xl text-[#F9FAFB] mt-3">{c.value}</p>
            <p className="text-xs text-[#9CA3AF] mt-1">{c.label}</p>
          </Link>
        ))}
      </div>

      <div className="mt-8 bg-[#111827] border border-[#1F2937] rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#1F2937]">
          <h2 className="font-display font-bold text-sm text-[#F9FAFB]">Recent Leads</h2>
          <Link to="/admin/leads" className="flex items-center gap-1 text-xs font-bold text-[#10B981] hover:gap-2 transition-all" data-testid="dashboard-view-leads">
            View all <ArrowRight size={13} />
          </Link>
        </div>
        {stats.recent_leads.length === 0 ? (
          <p className="px-5 py-8 text-sm text-[#6B7280]">No leads yet. They'll appear here as enquiries come in.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-[#6B7280] border-b border-[#1F2937]">
                  <th className="px-5 py-3">Name</th>
                  <th className="px-5 py-3">Interest</th>
                  <th className="px-5 py-3">Source</th>
                  <th className="px-5 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {stats.recent_leads.map((l) => (
                  <tr key={l.id} className="border-b border-[#1F2937]/50 last:border-0">
                    <td className="px-5 py-3.5 font-semibold text-[#F9FAFB]">{l.name}</td>
                    <td className="px-5 py-3.5 text-[#9CA3AF]">{l.interest || "—"}</td>
                    <td className="px-5 py-3.5 text-[#9CA3AF]">{l.source}</td>
                    <td className="px-5 py-3.5">
                      <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold ${STATUS_COLORS[l.status] || "bg-slate-500/15 text-slate-400"}`}>
                        {l.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
